import { FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ConnectorConfig } from '@/types/api';

interface DocumentMappingsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly config: ConnectorConfig;
}

function formatRelativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffHr / 24);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${diffDays}d ago`;
}

export function DocumentMappingsDialog({
  open,
  onOpenChange,
  config,
}: DocumentMappingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Document Mappings — {config.displayName}
          </DialogTitle>
        </DialogHeader>

        {config.documentMappings.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center">
            <FileText className="mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              No documents mapped to this connector yet.
            </p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <div className="flex items-center gap-4 border-b border-border bg-muted/30 px-4 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <div className="flex-1">Document Title</div>
              <div className="w-40">External ID</div>
              <div className="w-28 text-right">Last Synced</div>
            </div>
            {config.documentMappings.map((m) => (
              <div
                key={m.mappingId}
                className="flex items-center gap-4 border-b border-border px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {m.documentTitle}
                  </p>
                </div>
                <div className="w-40">
                  <code className="font-mono text-xs text-muted-foreground">
                    {m.externalDocumentId.slice(0, 16)}
                  </code>
                </div>
                <div className="w-28 text-right text-xs text-muted-foreground">
                  {m.lastSyncedAt
                    ? formatRelativeTime(m.lastSyncedAt)
                    : 'Never'}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
